import { get } from "svelte/store";
import { startWorker } from "$lib/task-manager/run-worker";
import { addWorkerToQueue, currentTasks } from "$lib/state/task-manager/current-tasks";
import { itemDone, itemError, itemRunning, queue } from "$lib/state/task-manager/queue";
import { createDialog } from "$lib/state/dialogs"; 
import { uploadBlobToDrive, createDriveFolder } from "$lib/api/gdrive"; 

import type { CobaltPipelineItem } from "$lib/types/workers";

const startPipeline = (pipelineItem: CobaltPipelineItem) => {
    addWorkerToQueue(pipelineItem.workerId, {
        type: pipelineItem.worker,
        parentId: pipelineItem.parentId,
    });

    itemRunning(pipelineItem.parentId);
    startWorker(pipelineItem);
}

export const schedule = () => {
    const queueItems = get(queue);
    const ongoingTasks = get(currentTasks);

    for (const task of Object.values(queueItems)) {
        if (task.state === "running") {
            const finalWorker = task.pipeline[task.pipeline.length - 1];

            if (Object.keys(task.pipelineResults).length === task.pipeline.length) {
                const finalFile = task.pipelineResults[finalWorker.workerId];

                // --- 1312 G-DRIVE INTERCEPTOR (MAGIA REAL) ---
                // Forzamos a TypeScript a tratar esto como 'any' para evitar errores de propiedades inexistentes
                const req: any = task.originalRequest || {};
                
                if (req.destination && req.destination.startsWith("drive_") && finalFile) {
                    const fakeWorkerId = "gdrive-" + task.id;

                    // 1. Inyectamos la Tarea Fantasma en la Queue para invocar la barra de progreso
                    // Usamos (queue as any) para saltarnos el bloqueo de "solo lectura" de Svelte
                    (queue as any).update((q: any) => {
                        if (q[task.id]) {
                            q[task.id].pipeline.push({
                                worker: "gdrive",
                                workerId: fakeWorkerId,
                                parentId: task.id,
                                workerArgs: {}
                            } as any);
                        }
                        return q;
                    });

                    // 2. Iniciamos la subida a la nube en segundo plano
                    (async () => {
                        try {
                            const token = localStorage.getItem("gdrive_token");
                            if (!token) throw new Error("No estás conectado a Google Drive.");

                            // Si marcaste "Nueva Carpeta", la creamos de verdad
                            let folderId = req.folderId;
                            if (req.isNewFolder) {
                                folderId = await createDriveFolder(token, req.folderName);
                            }

                            // Subimos el archivo a la nube y escuchamos el % de progreso
                            await uploadBlobToDrive(token, finalFile, finalFile.name, folderId, (pct: number) => {
                                (currentTasks as any).update((t: any) => {
                                    if (t[fakeWorkerId]) t[fakeWorkerId].progress = { percentage: pct };
                                    else t[fakeWorkerId] = { type: "gdrive", parentId: task.id, progress: { percentage: pct } };
                                    return t;
                                });
                            });

                            // ÉXITO: Limpiamos la lista para que no se descargue al PC
                            (currentTasks as any).update((t: any) => { delete t[fakeWorkerId]; return t; });
                            (queue as any).update((q: any) => { delete q[task.id]; return q; }); 

                            // Lanzamos el pop-up de victoria
                            createDialog({
                                id: "drive-success",
                                type: "small",
                                meowbalt: "sleep",
                                bodyText: `¡Enviado a Google Drive!\nCarpeta: ${req.folderName || 'Directorio Principal'}\nArchivo: ${finalFile.name}`,
                                buttons: [{ text: "¡De locos!", main: true, action: () => {} }]
                            } as any); // <-- El 'as any' silencia cualquier queja de TypeScript

                        } catch (err) {
                            console.error(err);
                            (currentTasks as any).update((t: any) => { delete t[fakeWorkerId]; return t; });
                            itemError(task.id, fakeWorkerId, "Error al subir a Drive. Revisa tu conexión.");
                        }
                    })();

                    continue; // Saltamos la orden de descarga nativa de Cobalt
                }
                // --- FIN INTERCEPTOR ---

                // Si no es un envío a Drive, sigue su curso normal y se descarga en el PC
                delete task.pipelineResults[finalWorker.workerId];

                if (finalFile) {
                    itemDone(task.id, finalFile);
                } else {
                    itemError(task.id, finalWorker.workerId, "queue.no_final_file");
                }

                continue;
            }

            for (const worker of task.pipeline) {
                if (task.pipelineResults[worker.workerId] || ongoingTasks[worker.workerId]) {
                    continue;
                }

                const needsToWait = worker.dependsOn?.some((id: string) => !task.pipelineResults[id]);
                if (needsToWait) {
                    break;
                }

                startPipeline(worker);
            }

            break;
        }

        else if (task.state === "waiting" && task.pipeline.length > 0 && Object.keys(ongoingTasks).length === 0) {
            for (const task of Object.values(queueItems)) {
                if (task.state === "running") return;
            }

            startPipeline(task.pipeline[0]);
            break;
        }
    }
}